// Admin model performance dashboard — /admin/models
//
// Aggregates usage_events for the last 30 days per model:
//   - calls: count of successful rows
//   - revenue: credits_charged * $0.025 retail
//   - cogs: sum(provider_cost_micros) / 1_000_000
//   - margin %: (revenue - cogs) / revenue
//
// Spot models where COGS creeps above retail. Active toggle flips
// ai_models.active.
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { db } from "@/lib/billing";

const CREDIT_RETAIL_USD = 0.025;  // $0.025 per credit ⇒ $25 for 1000 credits

type ModelRow = {
  key: string;
  label: string;
  provider: string;
  task_type: string;
  credits_cost: number;
  active: boolean;
};

type UsageRow = {
  model_key: string | null;
  credits_charged: number;
  provider_cost_micros: number | null;
  status: string;
  created_at: string;
};

type CreditTransactionRow = {
  id: string;
  company_id: string;
  website_id: string | null;
  article_id: string | null;
  job_id: string | null;
  delta: number;
  reason: string;
  idempotency_key: string;
  metadata: Record<string, unknown> | null;
  balance_after: number;
  created_at: string;
};

type WalletRow = {
  company_id: string;
  balance: number;
  lifetime_granted: number;
  lifetime_consumed: number;
};

async function fetchModels(): Promise<ModelRow[]> {
  const { data, error } = await db
    .from("ai_models")
    .select("key, label, provider, task_type, credits_cost, active")
    .order("sort_order");
  if (error) throw error;
  return (data as ModelRow[]) ?? [];
}

async function fetchRecentUsage(): Promise<UsageRow[]> {
  const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
  const { data, error } = await db
    .from("usage_events")
    .select("model_key, credits_charged, provider_cost_micros, status, created_at")
    .gte("created_at", since)
    .limit(50_000);
  if (error) throw error;
  return (data as UsageRow[]) ?? [];
}

async function fetchCreditTransactions(): Promise<CreditTransactionRow[]> {
  const { data, error } = await db
    .from("credit_transactions")
    .select("id, company_id, website_id, article_id, job_id, delta, reason, idempotency_key, metadata, balance_after, created_at")
    .order("created_at", { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data as unknown as CreditTransactionRow[]) ?? [];
}

async function fetchWallets(): Promise<WalletRow[]> {
  const { data, error } = await db
    .from("credit_wallets")
    .select("company_id, balance, lifetime_granted, lifetime_consumed")
    .order("updated_at", { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data as unknown as WalletRow[]) ?? [];
}

export default function AdminModelsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [activeOnly, setActiveOnly] = useState(false);

  const models = useQuery({ queryKey: ["admin-ai-models"], queryFn: fetchModels });
  const usage = useQuery({ queryKey: ["admin-usage-30d"], queryFn: fetchRecentUsage });
  const transactions = useQuery({ queryKey: ["admin-credit-transactions"], queryFn: fetchCreditTransactions });
  const wallets = useQuery({ queryKey: ["admin-credit-wallets"], queryFn: fetchWallets });

  const stats = useMemo(() => {
    const map = new Map<string, { calls: number; credits: number; cogsMicros: number }>();
    for (const row of usage.data ?? []) {
      if (row.status !== "success" || !row.model_key) continue;
      const cur = map.get(row.model_key) ?? { calls: 0, credits: 0, cogsMicros: 0 };
      cur.calls += 1;
      cur.credits += Number(row.credits_charged ?? 0);
      cur.cogsMicros += Number(row.provider_cost_micros ?? 0);
      map.set(row.model_key, cur);
    }
    return map;
  }, [usage.data]);

  async function toggleActive(m: ModelRow) {
    try {
      const { error } = await db.from("ai_models").update({ active: !m.active }).eq("key", m.key);
      if (error) throw error;
      qc.invalidateQueries({ queryKey: ["admin-ai-models"] });
    } catch (e) {
      toast({ variant: "destructive", description: e instanceof Error ? e.message : String(e) });
    }
  }

  if (models.isLoading || usage.isLoading || transactions.isLoading || wallets.isLoading) {
    return (
      <div className="container mx-auto py-8 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}
      </div>
    );
  }

  const rows = (models.data ?? []).filter((m) => (activeOnly ? m.active : true));
  const walletTotals = (wallets.data ?? []).reduce(
    (acc, wallet) => ({
      balance: acc.balance + Number(wallet.balance || 0),
      granted: acc.granted + Number(wallet.lifetime_granted || 0),
      consumed: acc.consumed + Number(wallet.lifetime_consumed || 0),
    }),
    { balance: 0, granted: 0, consumed: 0 },
  );
  const testRows = (transactions.data ?? []).filter((tx) => /^(concurrency-test|debug-|test-)/i.test(tx.idempotency_key));

  return (
    <div className="container mx-auto py-6 space-y-4 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">AI models — 30-day performance</h1>
          <p className="text-sm text-muted-foreground">Spot models where COGS is creeping above retail.</p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={activeOnly} onCheckedChange={setActiveOnly} />
          Active only
        </label>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total wallet balance</p>
            <p className="mt-1 text-2xl font-bold">{walletTotals.balance}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Lifetime granted</p>
            <p className="mt-1 text-2xl font-bold">{walletTotals.granted}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Lifetime consumed</p>
            <p className="mt-1 text-2xl font-bold">{walletTotals.consumed}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Test/debug ledger rows</p>
            <p className="mt-1 text-2xl font-bold">{testRows.length}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Models</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Model</TableHead>
                <TableHead>Task</TableHead>
                <TableHead className="text-right">Calls (30d)</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead className="text-right">COGS</TableHead>
                <TableHead className="text-right">Margin</TableHead>
                <TableHead className="text-center">Active</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((m) => {
                const s = stats.get(m.key) ?? { calls: 0, credits: 0, cogsMicros: 0 };
                const revenue = s.credits * CREDIT_RETAIL_USD;
                const cogs = s.cogsMicros / 1_000_000;
                const margin = revenue > 0 ? (revenue - cogs) / revenue : null;
                const marginPct = margin === null ? "—" : `${Math.round(margin * 100)}%`;
                const negative = margin !== null && margin < 0.2;
                return (
                  <TableRow key={m.key}>
                    <TableCell>
                      <div className="font-medium">{m.label}</div>
                      <div className="text-xs text-muted-foreground">{m.provider} · {m.key}</div>
                    </TableCell>
                    <TableCell><Badge variant="outline">{m.task_type}</Badge></TableCell>
                    <TableCell className="text-right">{s.calls.toLocaleString()}</TableCell>
                    <TableCell className="text-right">${revenue.toFixed(2)}</TableCell>
                    <TableCell className="text-right">${cogs.toFixed(2)}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant={negative ? "destructive" : "secondary"}>{marginPct}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch checked={m.active} onCheckedChange={() => toggleActive(m)} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Credit usage ledger</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Relation</TableHead>
                <TableHead>Idempotency</TableHead>
                <TableHead className="text-right">Delta</TableHead>
                <TableHead className="text-right">Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(transactions.data ?? []).map((tx) => {
                const isTest = /^(concurrency-test|debug-|test-)/i.test(tx.idempotency_key);
                return (
                  <TableRow key={tx.id}>
                    <TableCell className="whitespace-nowrap text-xs">{new Date(tx.created_at).toLocaleString()}</TableCell>
                    <TableCell>
                      <Badge variant={tx.delta < 0 ? "destructive" : "secondary"}>{tx.reason}</Badge>
                      {isTest && <Badge variant="outline" className="ml-2">test/debug</Badge>}
                    </TableCell>
                    <TableCell className="max-w-[260px] text-xs text-muted-foreground">
                      <div>Company: {tx.company_id.slice(0, 8)}</div>
                      {tx.website_id && <div>Website: {tx.website_id.slice(0, 8)}</div>}
                      {tx.article_id && <div>Article: {tx.article_id.slice(0, 8)}</div>}
                      {tx.job_id && <div>Job: {tx.job_id.slice(0, 8)}</div>}
                    </TableCell>
                    <TableCell className="max-w-[260px] truncate text-xs">{tx.idempotency_key}</TableCell>
                    <TableCell className="text-right font-semibold">{tx.delta > 0 ? `+${tx.delta}` : tx.delta}</TableCell>
                    <TableCell className="text-right">{tx.balance_after}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
